import { useEffect, useState } from 'react';
import { createRootRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';

import { Shell } from '@/app/shell/shell';
import { useMe } from '@/shared/api';
import { useRealtimeStream } from '@/shared/realtime/use-realtime-stream';
import { useLiveNotificationToasts } from '@/shared/realtime/use-live-notification-toasts';
import { useAuthStore } from '@/shared/stores/auth.store';
import { useAuthBootstrap, useSessionDeferred } from '@/shared/stores/use-auth-bootstrap';
import messages from '@/shared/i18n/en/shell';
import { useT } from '@/shared/i18n/react';
import { TooltipProvider } from '@/shared/ui/tooltip';

import { NotFoundPage, RuntimeErrorPage } from './access-denied';

const PUBLIC_PATHS = new Set([
  '/login',
  '/register',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/confirm-email-change',
]);

function isPublicPath(path: string): boolean {
  // `/invites/<token>` shows a preview to unauthenticated recipients of an
  // invitation email; they sign in or sign up from the preview page.  The
  // auth-guard must skip this prefix so the redirect-to-login doesn't fire.
  if (path.startsWith('/invites/')) return true;
  // `/share/<token>` is the anonymous public read-only viewer for
  // project/task share links (Track C step 4).  Same rationale —
  // the token IS the auth; we render a stripped shell with just the
  // resource and a marketing footer.
  if (path.startsWith('/share/')) return true;
  return PUBLIC_PATHS.has(path) || path.startsWith('/auth');
}

function RootLayout() {
  const { location } = useRouterState();
  const navigate = useNavigate();
  const accessToken = useAuthStore((s) => s.accessToken);
  const hasUser = useAuthStore((s) => !!s.user);
  const sessionDeferred = useSessionDeferred();

  // Re-mint the in-memory access token from the httpOnly refresh cookie on
  // load.  Until this resolves we hold the auth-guard so a valid session isn't
  // bounced to /login during the silent refresh.
  const bootstrapped = useAuthBootstrap();
  // Offline cold launch: no access token can be minted without a network, but
  // a remembered user may open the app from the offline cache (My Work, Inbox).
  // Requests stay paused until reconnect; the bootstrap then renews the session
  // (or clears it on a definitive 401, which lands here as a /login redirect).
  const offlineSession = bootstrapped && !accessToken && hasUser && sessionDeferred;

  // Bootstrap /me on app load when we have a token. Failures (e.g. expired refresh) clear the
  // store inside the api client, which then navigates us to /login through the guard below.
  useMe();

  // SSE realtime — only when we have a session. Disconnects automatically on
  // logout / public route navigation.
  const realtimeEnabled = !!accessToken && !isPublicPath(location.pathname);
  useRealtimeStream({ enabled: realtimeEnabled });
  // Live, dismissible in-app toasts for important notifications as they arrive.
  useLiveNotificationToasts({ enabled: realtimeEnabled });

  // Auth-guard: shell-pages require a session; redirect to /login when missing.
  // Preserve the original URL as `?next=` so the login page can navigate back
  // after successful sign-in (per docs/22 §7 "post-login redirect").
  useEffect(() => {
    if (bootstrapped && !accessToken && !offlineSession && !isPublicPath(location.pathname)) {
      const next = location.pathname + (location.searchStr ? location.searchStr : '');
      void navigate({
        to: '/login',
        search: next && next !== '/' ? { next } : {},
        replace: true,
      });
    }
  }, [bootstrapped, accessToken, offlineSession, location.pathname, location.searchStr, navigate]);

  // Safety net: on every URL change, reset any Radix body-pointer-events lock
  // that survived a Dialog close racing with a navigation.  Radix sets
  // `<body style="pointer-events:none">` while a Dialog is open and clears it
  // on close — but if the close-cleanup is interrupted by a route transition,
  // the lock can stick and block all clicks.  Cheap to always reset.
  useEffect(() => {
    if (document.body.style.pointerEvents) {
      document.body.style.pointerEvents = '';
    }
  }, [location.pathname, location.search]);

  if (isPublicPath(location.pathname)) {
    return (
      <TooltipProvider delayDuration={200}>
        <Outlet />
      </TooltipProvider>
    );
  }

  if (!bootstrapped) {
    // Silent refresh in flight — don't flash the shell or redirect a session
    // that's about to be restored.  The boot refresh retries 429/5xx/offline
    // for up to ~30s, so after a beat say what's happening instead of a blank page.
    return <BootPlaceholder />;
  }

  if (!accessToken && !offlineSession) {
    // Boot resolved with no session — render nothing while the guard redirects.
    return null;
  }

  // OAuth consent (one-click MCP connectors) needs a session but no app chrome:
  // the user arrives from claude.ai / ChatGPT and leaves straight back there.
  if (location.pathname.startsWith('/oauth/')) {
    return (
      <TooltipProvider delayDuration={200}>
        <Outlet />
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Shell>
        <Outlet />
      </Shell>
    </TooltipProvider>
  );
}

/**
 * Blank for the usual sub-second boot; a calm status line if it drags on.  A
 * remembered user's boot keeps retrying while the API is unreachable (see
 * useAuthBootstrap), so after a while offer a way out instead of a dead end.
 */
function BootPlaceholder() {
  const t = useT(messages);
  const [stage, setStage] = useState<'blank' | 'connecting' | 'stalled'>('blank');
  useEffect(() => {
    const show = setTimeout(() => setStage('connecting'), 1500);
    const stall = setTimeout(() => setStage('stalled'), 20_000);
    return () => {
      clearTimeout(show);
      clearTimeout(stall);
    };
  }, []);
  if (stage === 'blank') return null;
  return (
    <div className="min-h-dvh grid place-items-center px-4 bg-[var(--bg)] text-sm text-[var(--fg-muted)]">
      <div className="flex flex-col items-center gap-3 text-center">
        <p role="status" aria-live="polite">
          {stage === 'stalled' ? t('stalled') : t('connecting')}
        </p>
        {stage === 'stalled' && (
          <a href="/login" className="min-h-10 inline-flex items-center underline text-[var(--fg)]">
            {t('signInAgain')}
          </a>
        )}
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  component: RootLayout,
  // Any URL that doesn't match a registered route renders the privacy-safe
  // not-found page (same body as the 403 surface so we don't leak existence).
  notFoundComponent: NotFoundPage,
  // Runtime errors (loader throws, component crash) surface a calm
  // "something went wrong" page with a reset button and collapsible
  // technical details.  Sentry sees the full error via the global handler.
  errorComponent: RuntimeErrorPage,
});
